import React from 'react';
import { Calendar, Users, Edit, Copy, Eye, Download, DollarSign, FileText, Trash2, CheckCircle, Send } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';

interface ItineraryCardProps {
  itinerary: any;
  onEdit: (itinerary: any) => void;
  onCopy: (itinerary: any) => void;
  onPreview: (itinerary: any) => void;
  onDownload: (id: number) => void;
  onSetPrice: (itinerary: any) => void;
  onAgentDownload: (id: number) => void;
  onStatusChange: (id: number, status: string, currentStatus: string) => void;
  onDelete?: (id: number) => void;
  onConvert?: (id: number) => void;
  onRelease?: (id: number) => void;
  onOpenOperationalCost?: (id: number) => void;
  viewMode: 'grid' | 'list';
}

const ItineraryCard: React.FC<ItineraryCardProps> = ({
  itinerary,
  onEdit,
  onCopy,
  onPreview,
  onDownload,
  onSetPrice,
  onAgentDownload,
  onStatusChange,
  onDelete,
  onConvert,
  onRelease,
  onOpenOperationalCost,
  viewMode,
}) => {
  const { canDelete, canConvert } = useAuthContext();
  const id = itinerary.id || itinerary.itinerary_id;
  const wynId = itinerary.wyn_itinerary_id || `WH_${id}`;
  const name = itinerary.itinerary_name || itinerary.basic_info?.itinerary_name || 'Untitled';
  const agencyName = itinerary.agency_name || itinerary.basic_info?.agency_name || '';
  const rawTravelDate = itinerary.travel_date || itinerary.basic_info?.travel_date || '';
  const travelDate = rawTravelDate ? new Date(rawTravelDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-') : '';
  const noPax = itinerary.no_of_pax || itinerary.basic_info?.no_of_pax || 0;
  const noChildren = itinerary.no_of_children || itinerary.basic_info?.no_of_children || 0;
  const status = itinerary.cost_breakdown?.status || itinerary.status || 'PENDING_CONFIRMATION';
  const guestType = itinerary.guest_type || itinerary.basic_info?.guest_type || 'FIT';
  
  // Check if editing should be disabled (CONVERTED status)
  const isEditDisabled = status === 'CONVERTED';

  const statusColors = {
    PENDING_CONFIRMATION: 'bg-orange-100 text-orange-700 border-orange-200',
    TENTATIVE: 'bg-blue-100 text-blue-700 border-blue-200',
    CONVERTED: 'bg-green-100 text-green-700 border-green-200',
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="mb-2">
              <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-sm font-mono font-bold rounded">#{wynId}</span>
            </div>
            <h3 className="font-semibold text-base text-gray-900 mb-2 break-words">{name}</h3>
            <div className="flex items-center gap-3 mb-3 md:mb-0">
              {travelDate && <span className="flex items-center gap-1 text-xs text-gray-600"><Calendar className="w-4 h-4" />{travelDate}</span>}
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">{guestType}</span>
              <div className="md:hidden">
                {canConvert ? (
                  <select
                    value={status}
                    onChange={(e) => onStatusChange(id, e.target.value, status)}
                    className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer ${statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                  >
                    <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                    <option value="TENTATIVE">Tentative</option>
                    <option value="CONVERTED">Converted</option>
                  </select>
                ) : (
                  status === 'CONVERTED' ? (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColors[status as keyof typeof statusColors]}`}>
                      Converted
                    </span>
                  ) : (
                    <select
                      value={status}
                      onChange={(e) => onStatusChange(id, e.target.value, status)}
                      className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer ${statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                    >
                      <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                      <option value="TENTATIVE">Tentative</option>
                    </select>
                  )
                )}
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            {canConvert ? (
              <select
                value={status}
                onChange={(e) => onStatusChange(id, e.target.value, status)}
                className={`px-3 py-1 rounded-full text-xs font-medium border cursor-pointer ${statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
              >
                <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                <option value="TENTATIVE">Tentative</option>
                <option value="CONVERTED">Converted</option>
              </select>
            ) : (
              status === 'CONVERTED' ? (
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[status as keyof typeof statusColors]}`}>
                  Converted
                </span>
              ) : (
                <select
                  value={status}
                  onChange={(e) => onStatusChange(id, e.target.value, status)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border cursor-pointer ${statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                >
                  <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                  <option value="TENTATIVE">Tentative</option>
                </select>
              )
            )}
            <button onClick={() => onSetPrice(itinerary)} className="p-2 hover:bg-yellow-100 text-yellow-700 rounded-lg transition-colors" title="Set Price"><DollarSign className="w-4 h-4" /></button>
            <button onClick={() => onPreview(itinerary)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Preview"><Eye className="w-4 h-4" /></button>
            <button 
              onClick={() => onEdit(itinerary)} 
              disabled={isEditDisabled}
              className={`p-2 rounded-lg transition-colors ${
                isEditDisabled 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'hover:bg-blue-100 text-blue-600'
              }`} 
              title={isEditDisabled ? 'Cannot edit CONVERTED itinerary' : 'Edit'}
            >
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={() => onCopy(itinerary)} className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition-colors" title="Copy"><Copy className="w-4 h-4" /></button>
            <button onClick={() => onDownload(id)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Download PDF"><Download className="w-4 h-4" /></button>
            <button onClick={() => onAgentDownload(id)} className="p-2 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors" title="Agent Itinerary"><FileText className="w-4 h-4" /></button>
            {onOpenOperationalCost && status === 'CONVERTED' && (
              <button onClick={() => onOpenOperationalCost(id)} className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition-colors" title="Operational Cost"><DollarSign className="w-4 h-4" /></button>
            )}
            {canConvert && onConvert && <button onClick={() => onConvert(id)} className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition-colors" title="Convert"><CheckCircle className="w-4 h-4" /></button>}
            {canConvert && onRelease && <button onClick={() => onRelease(id)} className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors" title="Release"><Send className="w-4 h-4" /></button>}
            {canDelete && onDelete && <button onClick={() => onDelete(id)} className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>}
          </div>
          <div className="flex md:hidden overflow-x-auto gap-2 pb-1">
            <button onClick={() => onPreview(itinerary)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0" title="Preview"><Eye className="w-4 h-4" /></button>
            <button 
              onClick={() => onEdit(itinerary)} 
              disabled={isEditDisabled}
              className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                isEditDisabled 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'hover:bg-blue-100 text-blue-600'
              }`} 
              title={isEditDisabled ? 'Cannot edit CONVERTED itinerary' : 'Edit'}
            >
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={() => onCopy(itinerary)} className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition-colors flex-shrink-0" title="Copy"><Copy className="w-4 h-4" /></button>
            <button onClick={() => onSetPrice(itinerary)} className="p-2 hover:bg-yellow-100 text-yellow-700 rounded-lg transition-colors flex-shrink-0" title="Set Price"><DollarSign className="w-4 h-4" /></button>
            <button onClick={() => onDownload(id)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0" title="Download PDF"><Download className="w-4 h-4" /></button>
            <button onClick={() => onAgentDownload(id)} className="p-2 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors flex-shrink-0" title="Agent Itinerary"><FileText className="w-4 h-4" /></button>
            {onOpenOperationalCost && status === 'CONVERTED' && (
              <button onClick={() => onOpenOperationalCost(id)} className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition-colors flex-shrink-0" title="Operational Cost"><DollarSign className="w-4 h-4" /></button>
            )}
            {canConvert && onConvert && <button onClick={() => onConvert(id)} className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition-colors flex-shrink-0" title="Convert"><CheckCircle className="w-4 h-4" /></button>}
            {canConvert && onRelease && <button onClick={() => onRelease(id)} className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors flex-shrink-0" title="Release"><Send className="w-4 h-4" /></button>}
            {canDelete && onDelete && <button onClick={() => onDelete(id)} className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors flex-shrink-0" title="Delete"><Trash2 className="w-4 h-4" /></button>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-blue-400 transition-all">
      {/* Status Bar */}
      <div className={`h-1 ${status === 'CONVERTED' ? 'bg-green-500' : status === 'TENTATIVE' ? 'bg-blue-500' : 'bg-orange-500'}`}></div>
      
      <div className="p-4">
        {/* Header */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono font-semibold text-blue-600">#{wynId}</span>
            {canConvert ? (
              <select
                value={status}
                onChange={(e) => onStatusChange(id, e.target.value, status)}
                className={`px-2 py-1 rounded-md text-xs font-medium cursor-pointer ${statusColors[status as keyof typeof statusColors]}`}
              >
                <option value="PENDING_CONFIRMATION">Pending</option>
                <option value="TENTATIVE">Tentative</option>
                <option value="CONVERTED">Converted</option>
              </select>
            ) : (
              status === 'CONVERTED' ? (
                <span className={`px-2 py-1 rounded-md text-xs font-medium ${statusColors[status as keyof typeof statusColors]}`}>Converted</span>
              ) : (
                <select
                  value={status}
                  onChange={(e) => onStatusChange(id, e.target.value, status)}
                  className={`px-2 py-1 rounded-md text-xs font-medium cursor-pointer ${statusColors[status as keyof typeof statusColors]}`}
                >
                  <option value="PENDING_CONFIRMATION">Pending</option>
                  <option value="TENTATIVE">Tentative</option>
                </select>
              )
            )}
          </div>
          <h3 className="font-semibold text-sm text-gray-900 line-clamp-2 mb-2">{name}</h3>
          {agencyName && <p className="text-xs text-gray-500 mb-2">{agencyName}</p>}
          <div className="flex items-center gap-3 text-xs text-gray-600">
            {travelDate && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{travelDate}</span>}
            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{noPax}A{noChildren > 0 && `, ${noChildren}C`}</span>
            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">{guestType}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="pt-3 border-t-2 border-gray-200">
          <div className="grid grid-cols-4 gap-1.5">
            <button onClick={() => onPreview(itinerary)} className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors" title="View"><Eye className="w-4 h-4 mx-auto" /></button>
            <button 
              onClick={() => onEdit(itinerary)} 
              disabled={isEditDisabled}
              className={`p-2 rounded-lg transition-colors ${
                isEditDisabled 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`} 
              title={isEditDisabled ? 'Cannot edit CONVERTED itinerary' : 'Edit'}
            >
              <Edit className="w-4 h-4 mx-auto" />
            </button>
            <button onClick={() => onCopy(itinerary)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors" title="Copy"><Copy className="w-4 h-4 mx-auto" /></button>
            <button onClick={() => onSetPrice(itinerary)} className="p-2 hover:bg-yellow-50 rounded-lg text-yellow-600 transition-colors" title="Price"><DollarSign className="w-4 h-4 mx-auto" /></button>
            <button onClick={() => onDownload(id)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors" title="PDF"><Download className="w-4 h-4 mx-auto" /></button>
            <button onClick={() => onAgentDownload(id)} className="p-2 hover:bg-purple-50 rounded-lg text-purple-600 transition-colors" title="Agent"><FileText className="w-4 h-4 mx-auto" /></button>
            {onOpenOperationalCost && status === 'CONVERTED' && (
              <button onClick={() => onOpenOperationalCost(id)} className="p-2 hover:bg-green-50 rounded-lg text-green-600 transition-colors" title="Op Cost"><DollarSign className="w-4 h-4 mx-auto" /></button>
            )}
            {canConvert && onConvert && <button onClick={() => onConvert(id)} className="p-2 hover:bg-green-50 rounded-lg text-green-600 transition-colors" title="Convert"><CheckCircle className="w-4 h-4 mx-auto" /></button>}
            {canDelete && onDelete && <button onClick={() => onDelete(id)} className="p-2 hover:bg-red-50 rounded-lg text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4 mx-auto" /></button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItineraryCard;
